/**
 * svgPreview — structural drop/section previews as SVG, delivered as
 * `data:image/svg+xml` URIs for use in a plain <img>.
 *
 * WHY SVG-in-<img> (not live DOM / RSD): these little wireframe drawings are
 * rendered a LOT — one per palette tile, one following the pointer, and (at far
 * zoom) one per section of every page on the canvas. As live DOM that's hundreds
 * of nodes reconciled on every zoom/pan. As an <img> the browser rasterizes each
 * unique drawing ONCE and the compositor reuses it; identical `src` strings are
 * shared. So the drawing is pure string generation (no React), memoized by key.
 *
 * ONE drawing language, reused: the palette tiles, the drag ghost, and the page
 * wireframe all call the same primitives — a section always looks the same
 * whether you're dragging it or looking at it placed on a page.
 *
 * Positions come from the grid's own `gridToPixel` (the same math the real grid
 * uses), so a preview mirrors the actual layout. Solid fills only.
 */
import { gridToPixel } from "@fredericrous/grid";

import { SECTION_TEMPLATES } from "./sectionTemplates.js";
import { PAGE_TEMPLATES } from "./pageTemplates.js";
import { mediaKindIcon, type MediaIconKind } from "./mediaKindIcon.js";

// Matches SECTION_GRID_CONFIG / PAGE_GRID_CONFIG.
const COLS = 12;
const ROW_H = 60;
const GAP = 8;
const PAD = 8;

// Natural drawing widths (px) — the <img> scales from here via viewBox.
const SECTION_W = 280;
const WIDGET_W = 190;
const PAGE_W = 210;
const NAV_H = 18; // page nav band
// Fixed natural height for a SECTION palette tile so every tile crops to the
// exact same window (sections' real heights vary wildly — Hero ~120, Contact
// ~540 — which cropped to a fixed tile height left short ones gapped and tall
// ones bleeding). Matches the tile's crop aspect (~SECTION_W : cropHeight, i.e.
// 280:58×(280/100)); content beyond it is clipped by the viewBox, shorter
// sections show white below. The drag ghost still uses the section's TRUE
// height (it's what you're dropping) — this is tile-only. Kept a touch SHORTER
// than the crop's content height so there's always a white gap before the
// crop's bottom border — otherwise a section whose content bleeds to the edge
// hides the light-grey border under its light-grey content.
const SECTION_TILE_H = 150;
// Crisp white margin (natural px) masked over the bottom of a tile's content so
// it ends cleanly instead of a widget being cut mid-box at the image edge.
const SECTION_TILE_BOTTOM = 14;

export const PREVIEW_ASPECT_W = {
  section: SECTION_W,
  widget: WIDGET_W,
  page: PAGE_W,
} as const;

interface ItemLike {
  readonly position: { x: number; y: number; w: number; h: number };
  readonly contentType?: string;
}

/** Fill for a widget box by content type — a small, deliberately flat palette. */
function fillFor(type?: string): string {
  switch (type) {
    case "image":
      return "#cbd5e1";
    case "button":
      return "#94a3b8";
    case "heading":
      return "#dbe2ea";
    case "card":
      return "#e2e8f0";
    case "form":
      return "#eef2ff";
    case "input":
      return "#ffffff"; // an empty field: white, outlined
    case "audio":
      return "#e0e7ff"; // waveform-player tint
    case "video":
      return "#e2e8f0"; // reads distinct from text behind the play icon
    case "spacer":
      return "#f8fafc"; // near-empty — it's blank space
    default: // text + anything else
      return "#f1f5f9";
  }
}

/** Stroke for a widget box, or null for filled solids that don't need one. */
function strokeFor(type?: string): string | null {
  return type === "image" || type === "button" ? null : "#e2e8f0";
}

function rect(
  x: number,
  y: number,
  w: number,
  h: number,
  fill: string,
  opts?: { stroke?: string | null; rx?: number },
): string {
  const rx = opts?.rx ?? 5;
  const s = opts?.stroke;
  return `<rect x="${r2(x)}" y="${r2(y)}" width="${r2(w)}" height="${r2(h)}" rx="${rx}" fill="${fill}"${
    s ? ` stroke="${s}"` : ""
  }/>`;
}

// Round to 2dp — keeps the data URI short (fewer bytes → smaller src strings).
function r2(n: number): number {
  return Math.round(n * 100) / 100;
}

const MEDIA_KINDS = new Set(["image", "video", "audio"]);

/** A media kind-icon centered in a box (x,y,w,h), for the far-zoom wireframe so
 *  image/video/audio read as more than flat rectangles.
 *
 *  Sized by the SAME rule as the DOM placeholders (80% of the tile's shorter
 *  side) so the glyph doesn't jump when the LOD band flips at EDIT_MIN_ZOOM.
 *  The cap is a fraction of the DRAWING width, not an absolute: a page
 *  wireframe is drawn at true page pixels (~1440 wide) while a palette tile is
 *  280, so one absolute cap can't serve both — 30px reads fine on a tile and is
 *  a speck on a page. 0.11 lands at ~158 on a full-width page (matching the DOM
 *  clamp of 160) and ~31 on a section tile (its previous 30).
 *
 *  Stroke stays in viewBox units (proportional to the glyph) rather than
 *  pre-divided to a fixed ~1.6px: at far zoom a 1.6px hairline renders sub-pixel
 *  and the icon reads as a smudge.
 *
 *  `clipY` is the drawing's visible bottom edge, when the caller crops (palette
 *  tiles do). A section tile crops at 150 while a tall widget's true box runs to
 *  272, so centering in the TRUE box drops the glyph under the crop mask — it
 *  reads as a half-drawn icon. Centering in the visible slice instead keeps it
 *  in the part of the block the user can actually see. */
function mediaIcon(
  kind: MediaIconKind,
  x: number,
  y: number,
  w: number,
  h: number,
  drawW: number,
  clipY?: number,
): string {
  const visH = (clipY != null ? Math.min(y + h, clipY) : y + h) - y;
  const s = Math.min(Math.min(w, visH) * 0.8, drawW * 0.11);
  if (s < 8) return ""; // too small to read
  const scale = s / 24;
  const inner = mediaKindIcon(kind, { color: "#94a3b8" });
  return `<g transform="translate(${r2(x + (w - s) / 2)} ${r2(
    y + (visH - s) / 2,
  )}) scale(${r2(scale)})">${inner}</g>`;
}

/**
 * Draw a set of grid items into a box at (ox, oy) of content width `w`, using
 * `gridToPixel` for each. Returns the rects markup and the bottom edge (so the
 * caller can size the frame / stack the next section).
 */
function itemsMarkup(
  items: readonly ItemLike[],
  ox: number,
  oy: number,
  w: number,
  /** Visible bottom edge, when the caller crops the drawing (palette tiles). */
  clipY?: number,
): { markup: string; bottom: number } {
  let bottom = oy + PAD;
  const markup = items
    .map((it) => {
      const g = gridToPixel(it.position, w, COLS, ROW_H, GAP, PAD);
      const x = ox + g.left;
      const y = oy + g.top;
      if (y + g.height > bottom) bottom = y + g.height;
      const box = rect(x, y, g.width, g.height, fillFor(it.contentType), {
        stroke: strokeFor(it.contentType),
      });
      const ct = it.contentType;
      const icon =
        ct && MEDIA_KINDS.has(ct)
          ? mediaIcon(ct as MediaIconKind, x, y, g.width, g.height, w, clipY)
          : "";
      return box + icon;
    })
    .join("");
  return { markup, bottom };
}

function svgDoc(w: number, h: number, inner: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${r2(w)} ${r2(
    h,
  )}" width="${r2(w)}" height="${r2(h)}" preserveAspectRatio="xMidYMin meet">${inner}</svg>`;
}

function toUri(svg: string): string {
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

// ── memo cache ────────────────────────────────────────────────────────────
// Templates are finite + stable, so cache their URIs by key forever.
const cache = new Map<string, PreviewSvg>();

export interface PreviewSvg {
  readonly uri: string;
  /** Natural width/height of the drawing → an <img> can set aspect-ratio. */
  readonly w: number;
  readonly h: number;
}

function memo(key: string, build: () => PreviewSvg): PreviewSvg {
  const hit = cache.get(key);
  if (hit) return hit;
  const v = build();
  cache.set(key, v);
  return v;
}

/**
 * A section filled with its widget boxes. Two presentations:
 *  • ghost (fixedH omitted) — a white rounded CARD with its own border, sized to
 *    the section's true content height (it's the thing you're dropping).
 *  • tile  (fixedH set) — a plain white rect of uniform height, NO border (the
 *    palette tile's crop container draws the border); content is top-aligned and
 *    clipped to fixedH so every tile crops identically.
 */
function sectionSvg(
  items: readonly ItemLike[],
  w: number,
  fixedH?: number,
): PreviewSvg {
  const { markup, bottom } = itemsMarkup(
    items,
    0,
    0,
    w,
    // Tile: content is cropped to fixedH and masked over the last
    // SECTION_TILE_BOTTOM px, so that's where the drawing stops being visible.
    fixedH != null ? fixedH - SECTION_TILE_BOTTOM : undefined,
  );
  const h = fixedH ?? bottom + PAD;
  if (fixedH != null) {
    // Tile: plain white ground (the crop container draws the border), content
    // top-aligned, then a crisp white band masking the bottom so content ends
    // with a clean margin (not cut mid-widget) and never touches the crop's
    // bottom border. Crisp/flat — no fade.
    const ground = rect(0, 0, w, h, "#ffffff", { rx: 0 });
    const bottomMask = rect(
      0,
      h - SECTION_TILE_BOTTOM,
      w,
      SECTION_TILE_BOTTOM,
      "#ffffff",
      { rx: 0 },
    );
    return { uri: toUri(svgDoc(w, h, ground + markup + bottomMask)), w, h };
  }
  const frame = rect(0.5, 0.5, w - 1, h - 1, "#ffffff", {
    stroke: "#e5e7eb",
    rx: 6,
  });
  return { uri: toUri(svgDoc(w, h, frame + markup)), w, h };
}

/** Section template preview. `tile` → the uniform-height palette-tile variant;
 *  otherwise the true-height drag ghost. */
export function sectionPreview(templateId: string, tile = false): PreviewSvg {
  return memo(`section:${templateId}:${tile ? "tile" : "full"}`, () => {
    const tpl = SECTION_TEMPLATES.find((t) => t.id === templateId);
    const items = tpl ? tpl.items() : [];
    return sectionSvg(items, SECTION_W, tile ? SECTION_TILE_H : undefined);
  });
}

/** A single leaf widget (image / video / audio / text / spacer) preview. */
export function widgetPreview(widgetType: string): PreviewSvg {
  return memo(`widget:${widgetType}`, () => {
    const w = WIDGET_W;
    const h =
      widgetType === "image" || widgetType === "video"
        ? 112
        : widgetType === "spacer"
          ? 40
          : widgetType === "audio"
            ? 56
            : 64;
    const frame = rect(0.5, 0.5, w - 1, h - 1, "#ffffff", {
      stroke: "#e5e7eb",
      rx: 6,
    });
    const bx = w * 0.05;
    const by = h * 0.14;
    const bw = w * 0.9;
    const bh = h * 0.72;
    if (widgetType === "audio") {
      // A play circle on the left + a strip of waveform bars — the player at rest.
      const box = rect(bx, by, bw, bh, "#eef2ff", { stroke: "#e2e8f0", rx: 4 });
      const cy = by + bh / 2;
      const r = Math.min(bw, bh) * 0.34;
      const ccx = bx + r + 6;
      const circle = `<circle cx="${r2(ccx)}" cy="${r2(cy)}" r="${r2(r)}" fill="#2563eb"/>`;
      const s = r * 0.5;
      const tri = `<path d="M${r2(ccx - s * 0.5)} ${r2(cy - s)} L${r2(
        ccx - s * 0.5,
      )} ${r2(cy + s)} L${r2(ccx + s * 0.8)} ${r2(cy)} Z" fill="#ffffff"/>`;
      const heights = [
        0.4, 0.7, 1, 0.6, 0.85, 0.5, 0.9, 0.65, 0.35, 0.75, 0.55,
      ];
      const x0 = ccx + r + 8;
      const x1 = bx + bw - 6;
      const step = (x1 - x0) / heights.length;
      const bw2 = Math.max(1.5, step * 0.55);
      const bars = heights
        .map((f, i) => {
          const barH = bh * 0.7 * f;
          return rect(x0 + i * step, cy - barH / 2, bw2, barH, "#93a4c8", {
            stroke: null,
            rx: 1,
          });
        })
        .join("");
      return {
        uri: toUri(svgDoc(w, h, frame + box + circle + tri + bars)),
        w,
        h,
      };
    }
    if (widgetType === "video") {
      // A dark player with a centred play triangle.
      const box = rect(bx, by, bw, bh, "#334155", { stroke: null, rx: 4 });
      const cx = bx + bw / 2;
      const cy = by + bh / 2;
      const s = Math.min(bw, bh) * 0.18;
      const tri = `<path d="M${r2(cx - s * 0.6)} ${r2(cy - s)} L${r2(
        cx - s * 0.6,
      )} ${r2(cy + s)} L${r2(cx + s)} ${r2(cy)} Z" fill="#ffffff"/>`;
      return { uri: toUri(svgDoc(w, h, frame + box + tri)), w, h };
    }
    const box = rect(bx, by, bw, bh, fillFor(widgetType), {
      stroke: strokeFor(widgetType),
    });
    return { uri: toUri(svgDoc(w, h, frame + box)), w, h };
  });
}

/** A page template preview: a nav band over the template's stacked sections. */
export function pagePreview(pageTemplateId: string): PreviewSvg {
  return memo(`page:${pageTemplateId}`, () => {
    const w = PAGE_W;
    const tpl = PAGE_TEMPLATES.find((t) => t.id === pageTemplateId);
    const nav = rect(0, 0, w, NAV_H, "#0f172a", { rx: 4 });
    let y = NAV_H + 6;
    let body = "";
    const sections = tpl?.sections ?? [];
    if (sections.length === 0) {
      // Blank page: a couple of faint placeholder blocks.
      body += rect(6, y, w - 12, 26, "#eef2ff", { rx: 4 });
      y += 26 + 6;
      body += rect(6, y, w - 12, 40, "#f1f5f9", { rx: 4 });
      y += 40 + 6;
    } else {
      for (const secId of sections) {
        const tplSec = SECTION_TEMPLATES.find((s) => s.id === secId);
        if (!tplSec) continue;
        const { markup, bottom } = itemsMarkup(tplSec.items(), 6, y, w - 12);
        const secH = bottom - y + PAD;
        body =
          body +
          rect(6, y, w - 12, secH, "#ffffff", { stroke: "#eef1f5", rx: 4 }) +
          markup;
        y = y + secH + 6;
      }
    }
    const h = y + 2;
    const frame = rect(0.5, 0.5, w - 1, h - 1, "#ffffff", {
      stroke: "#e5e7eb",
      rx: 6,
    });
    return { uri: toUri(svgDoc(w, h, frame + nav + body)), w, h };
  });
}

/**
 * Wireframe of a REAL section (far-zoom page render) from its live nested-grid
 * items — the same drawing language as the palette, drawn at the EXACT pixel box
 * the section occupies on the page (boxW × boxH) so the <img> fills it 1:1 (no
 * stretch, no LOD jump when the real grid takes over). Not memoized (content is
 * dynamic); an identical `src` is a no-op for the browser anyway.
 */
export function sectionWireframe(
  items: readonly ItemLike[],
  boxW: number,
  boxH: number,
): PreviewSvg {
  const { markup } = itemsMarkup(items, 0, 0, boxW);
  const frame = rect(0.5, 0.5, boxW - 1, boxH - 1, "#ffffff", {
    stroke: "#e5e7eb",
    rx: 6,
  });
  return { uri: toUri(svgDoc(boxW, boxH, frame + markup)), w: boxW, h: boxH };
}

// ── design-system widget tiles ────────────────────────────────────────────
// Each tile draws the control it adds, as a UI designer would sketch it in a
// component sheet: the real shape, a real short label, one cue for what makes
// it different (a pointer on a button, an arrow on a link, a caret in a field,
// a search glyph in an input group). Flat fills on a soft ground, one accent.
// Drawn at the tile's crop aspect (WIDGET_W × DS_TILE_H) so nothing is cut.

const DS_TILE_H = 110;
const INK = "#0f172a";
const MUTED = "#64748b";
const FAINT = "#cbd5e1";
const LINE = "#e2e8f0";
const GROUND = "#f8fafc";
const ACCENT = "#5b5bd6";
const ACCENT_SOFT = "#eef0ff";
const FONT =
  "-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica,Arial,sans-serif";

function label(
  x: number,
  y: number,
  text: string,
  size: number,
  fill: string,
  opts?: { weight?: number; anchor?: "start" | "middle" | "end" },
): string {
  return `<text x="${r2(x)}" y="${r2(y)}" font-family="${FONT}" font-size="${size}" font-weight="${
    opts?.weight ?? 500
  }" fill="${fill}" text-anchor="${opts?.anchor ?? "start"}" dominant-baseline="central">${text}</text>`;
}

/** A soft drop shadow under a box: the same shape, offset and faint. */
function shadow(
  x: number,
  y: number,
  w: number,
  h: number,
  rx: number,
): string {
  return `<rect x="${r2(x)}" y="${r2(y + 2)}" width="${r2(w)}" height="${r2(
    h,
  )}" rx="${rx}" fill="${INK}" fill-opacity="0.08"/>`;
}

/** The arrow pointer, tip at (x, y). */
function pointer(x: number, y: number): string {
  return `<path d="M${x} ${y} l0 15 l4 -3.6 l2.8 6 l2.4 -1.1 l-2.8 -5.9 l5.2 -0.4 Z" fill="${INK}" stroke="#ffffff" stroke-width="1.2" stroke-linejoin="round"/>`;
}

/** A 24-unit outline glyph (search / mail / pin / star) at (x, y), size s. */
function glyph(
  kind: "search" | "mail" | "pin" | "star",
  x: number,
  y: number,
  s: number,
  color: string,
): string {
  const paths = {
    search: `<circle cx="11" cy="11" r="7"/><path d="M20 20l-4-4"/>`,
    mail: `<rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/>`,
    pin: `<path d="M12 21s-7-6.3-7-11a7 7 0 0 1 14 0c0 4.7-7 11-7 11z"/><circle cx="12" cy="10" r="2.5"/>`,
    star: `<path d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3 6.5 20.2l1-6.2L3 9.6l6.2-.9z"/>`,
  } as const;
  return `<g transform="translate(${r2(x)} ${r2(y)}) scale(${r2(
    s / 24,
  )})" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${paths[kind]}</g>`;
}

function dsTileArt(tileId: string, w: number, h: number): string | null {
  const ground = rect(0.5, 0.5, w - 1, h - 1, GROUND, { stroke: LINE, rx: 6 });
  const cx = w / 2;
  const cy = h / 2;
  switch (tileId) {
    case "w-button": {
      const bw = 116;
      const bh = 36;
      const x = cx - bw / 2;
      const y = cy - bh / 2 - 4;
      return (
        ground +
        shadow(x, y, bw, bh, 8) +
        rect(x, y, bw, bh, ACCENT, { stroke: null, rx: 8 }) +
        label(cx, y + bh / 2, "Get started", 14, "#ffffff", {
          weight: 600,
          anchor: "middle",
        }) +
        pointer(x + bw - 18, y + bh - 10)
      );
    }
    case "w-link-button": {
      const bw = 124;
      const bh = 36;
      const x = cx - bw / 2;
      const y = cy - bh / 2;
      const ay = y + bh / 2;
      const ax = x + bw - 24;
      return (
        ground +
        rect(x, y, bw, bh, "#ffffff", { stroke: FAINT, rx: 8 }) +
        label(x + 16, ay, "Learn more", 14, INK, { weight: 600 }) +
        `<path d="M${ax} ${ay} h11 M${ax + 6} ${ay - 5} l5 5 l-5 5" fill="none" stroke="${ACCENT}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>` +
        // The address it goes to, under it: a link, not an action.
        rect(x + 16, y + bh + 10, 64, 5, LINE, { stroke: null, rx: 2.5 })
      );
    }
    case "w-inline": {
      // An empty one-row grid: a dashed row with a tick at each cell edge.
      const rx = 22;
      const rw = w - 44;
      const rh = 44;
      const ry = cy - rh / 2;
      const cells = 6;
      const ticks = Array.from({ length: cells - 1 }, (_, i) => {
        const tx = rx + (rw / cells) * (i + 1);
        return `<path d="M${r2(tx)} ${r2(ry + 12)} v${rh - 24}" stroke="${LINE}" stroke-width="1.5" stroke-linecap="round"/>`;
      }).join("");
      return (
        ground +
        `<rect x="${rx}" y="${r2(ry)}" width="${rw}" height="${rh}" rx="8" fill="#ffffff" stroke="${ACCENT}" stroke-width="1.5" stroke-dasharray="6 4"/>` +
        ticks
      );
    }
    case "w-heading": {
      const x = 18;
      return (
        rect(0.5, 0.5, w - 1, h - 1, "#ffffff", { stroke: LINE, rx: 6 }) +
        rect(x, 22, 34, 6, ACCENT, { stroke: null, rx: 3 }) +
        label(x, 50, "Big ideas", 26, INK, { weight: 700 }) +
        rect(x, 72, 140, 6, FAINT, { stroke: null, rx: 3 }) +
        rect(x, 84, 104, 6, LINE, { stroke: null, rx: 3 })
      );
    }
    case "w-card": {
      const x = 32;
      const y = 10;
      const cw = w - 64;
      const ch = h - 20;
      const pic = 38;
      return (
        ground +
        shadow(x, y, cw, ch, 8) +
        rect(x, y, cw, ch, "#ffffff", { stroke: LINE, rx: 8 }) +
        // The picture: a soft panel with a hill and a sun, top corners round.
        `<path d="M${x} ${y + pic} v${-(pic - 8)} a8 8 0 0 1 8 -8 h${cw - 16} a8 8 0 0 1 8 8 v${pic - 8} Z" fill="${ACCENT_SOFT}"/>` +
        `<path d="M${x} ${y + pic} l30 -16 l22 10 l18 -8 l${cw - 70} 14 Z" fill="#c7cbf7"/>` +
        `<circle cx="${x + cw - 22}" cy="${y + 13}" r="5" fill="#ffffff"/>` +
        rect(x + 12, y + pic + 10, 70, 7, INK, { stroke: null, rx: 3.5 }) +
        rect(x + 12, y + pic + 24, cw - 24, 5, FAINT, {
          stroke: null,
          rx: 2.5,
        }) +
        rect(x + 12, y + pic + 34, cw - 48, 5, FAINT, { stroke: null, rx: 2.5 })
      );
    }
    case "w-input": {
      const x = 18;
      const fw = w - 36;
      const fy = 46;
      const fh = 36;
      return (
        ground +
        label(x, 30, "Email", 13, INK, { weight: 600 }) +
        // Focused: an accent ring around the field.
        rect(x - 3, fy - 3, fw + 6, fh + 6, ACCENT_SOFT, {
          stroke: null,
          rx: 10,
        }) +
        rect(x, fy, fw, fh, "#ffffff", { stroke: ACCENT, rx: 7 }) +
        label(x + 12, fy + fh / 2, "you@site.com", 13, MUTED, { weight: 400 }) +
        rect(x + 98, fy + 10, 2, fh - 20, ACCENT, { stroke: null, rx: 1 })
      );
    }
    case "w-input-group": {
      const x = 16;
      const fw = w - 32;
      const fh = 40;
      const fy = cy - fh / 2;
      const addon = 40;
      return (
        ground +
        shadow(x, fy, fw, fh, 20) +
        rect(x, fy, fw, fh, "#ffffff", { stroke: FAINT, rx: 20 }) +
        glyph("search", x + 12, fy + fh / 2 - 9, 18, ACCENT) +
        `<path d="M${x + addon} ${fy + 10} v${fh - 20}" stroke="${LINE}" stroke-width="1.5"/>` +
        label(x + addon + 10, fy + fh / 2, "Search…", 13, MUTED, {
          weight: 400,
        })
      );
    }
    case "w-icon": {
      const s = 34;
      const cells: readonly ["star" | "search" | "mail" | "pin", number][] = [
        ["star", cx - 58],
        ["search", cx - 17],
        ["mail", cx + 24],
      ];
      return (
        ground +
        cells
          .map(([k, x], i) =>
            i === 0
              ? rect(x - 4, cy - s / 2 - 4, s + 8, s + 8, ACCENT_SOFT, {
                  stroke: null,
                  rx: 10,
                }) + glyph(k, x, cy - s / 2, s, ACCENT)
              : glyph(k, x, cy - s / 2, s, MUTED),
          )
          .join("")
      );
    }
    default:
      return null;
  }
}

/** A design-system widget tile's drawing (`w-…` tile ids), or null for a
 *  tile this file does not draw. */
export function dsWidgetPreview(tileId: string): PreviewSvg | null {
  if (!tileId.startsWith("w-")) return null;
  const hit = cache.get(`ds:${tileId}`);
  if (hit) return hit;
  const w = WIDGET_W;
  const h = DS_TILE_H;
  const art = dsTileArt(tileId, w, h);
  if (art === null) return null;
  return memo(`ds:${tileId}`, () => ({ uri: toUri(svgDoc(w, h, art)), w, h }));
}
